import { createAtom, type Atom } from '@xstate/store';

import { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import type { TrackSourceType } from '../../api/cc-smarts/models/TrackSourceType';
import type { ActiveContent, ContentDetails } from '../../types/activeContent';
import { AUDIO_CONTAINER_MODES, type AudioContainerMode } from '../../types/audioContainerMode';
import type {
	ConfluenceLength,
	ConfluenceTone,
} from '../ui/audio-player/__generated__/AudioContainerQuery.graphql';
import type { AudioFeatureMenuItem } from '../ui/audio-player/menu/AudioFeatureMenuGroup';

const DEFAULT_AUDIO_FEATURE = AudioFeatureType.BRIEFING;

export type AudioPlayerState = {
	activeContent: ActiveContent;
	activeFeature: AudioFeatureType;
	activeMenu?: AudioFeatureMenuItem;
	activeAudioContainerMode: AudioContainerMode;
	pendingContent: ActiveContent | null;
	isOpen: boolean;
	isHidden: boolean;
	isPlaying: boolean;
	isPaused: boolean;
	product: string;
	cloudId: string;
	activationId: string;
	customization: {
		tone: ConfluenceTone;
		length: ConfluenceLength;
		playbackSpeed: number;
	};
	playlistDefaultExpanded?: boolean;
	playImmediately?: boolean;
	playlistHasUnavailableContent: boolean;
	returnFocusRef?: React.RefObject<HTMLElement>;
	hasOpenDropdownMenu: boolean;
};

const DEFAULT_CUSTOMIZATION: AudioPlayerState['customization'] = {
	tone: 'PROFESSIONAL',
	length: 'SHORT',
	playbackSpeed: 1.0,
};

const initialState: AudioPlayerState = {
	activeContent: {
		contentId: '',
		spaceKey: '',
		contentType: '',
		subType: '',
		title: '',
		author: '',
		avatarUrl: '',
		supportingContent: [],
		interimSupportingContent: undefined,
		mainContentLink: '',
		emojiId: undefined,
	},
	activeFeature: DEFAULT_AUDIO_FEATURE,
	activeAudioContainerMode: AUDIO_CONTAINER_MODES.EXPANDED,
	pendingContent: null,
	isOpen: false,
	isHidden: false,
	isPlaying: false,
	isPaused: false,
	product: '',
	cloudId: '',
	activationId: '',
	customization: DEFAULT_CUSTOMIZATION,
	playlistDefaultExpanded: false,
	playImmediately: false,
	playlistHasUnavailableContent: false,
	returnFocusRef: undefined,
	hasOpenDropdownMenu: false,
};

export const audioPlayerState: Atom<AudioPlayerState> = createAtom<AudioPlayerState>(initialState);

export const setActiveContent = (activeContent: ActiveContent): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		activeContent,
		// Reset playback states when switching to a new track
		isPlaying: false,
		isPaused: false,
	}));
};

export const setActiveFeature = (activeFeature: AudioFeatureType): void => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeFeature }));
};

export const setActiveMenu = (activeMenu: AudioFeatureMenuItem | undefined): void => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeMenu }));
};

export const setActiveAudioContainerMode = (activeAudioContainerMode: AudioContainerMode): void => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeAudioContainerMode }));
};

export const openAudioPlayer = (
	contentToOpen: ActiveContent,
	options?: {
		isUninitialized?: boolean;
		playImmediately?: boolean;
		expandPlaylist?: boolean;
		returnFocusRef?: React.RefObject<HTMLElement>;
	},
): void => {
	audioPlayerState.set((prevState) => {
		// Check if the content is already active
		const isAlreadyActive = prevState.activeContent.contentId === contentToOpen.contentId;

		// Check if the content is already pending
		const isAlreadyPending = prevState.pendingContent?.contentId === contentToOpen.contentId;

		const updates: Partial<AudioPlayerState> = {};

		updates.isHidden = false;
		updates.playImmediately = options?.playImmediately || false;
		updates.playlistDefaultExpanded = options?.expandPlaylist || false;

		if (options?.playImmediately && !isAlreadyActive) {
			updates.isPaused = false;
			updates.isPlaying = false;
		}

		if (options?.returnFocusRef) {
			updates.returnFocusRef = options.returnFocusRef;
		}

		// Handle expandPlaylist logic
		if (options?.expandPlaylist) {
			// If player is not open, set active content and open the playlist
			if (!prevState.isOpen) {
				return { ...prevState, activeContent: contentToOpen, isOpen: true, ...updates };
			}
			// If player is already open, just expand the playlist (don't change active content)
			return {
				...prevState,
				...updates,
			};
		}

		// If content is already active or pending, only apply updates (like expandPlaylist)
		if (isAlreadyActive || isAlreadyPending) {
			return { ...prevState, ...updates };
		}

		// If the player is uninitialized or playImmediately is true, set the new content as active
		if (options?.isUninitialized || options?.playImmediately) {
			return { ...prevState, activeContent: contentToOpen, isOpen: true, ...updates };
		}

		// If player is already open, set new content as pending
		if (prevState.isOpen) {
			return { ...prevState, pendingContent: contentToOpen, ...updates };
		}
		// Otherwise, set as active content and open player
		return { ...prevState, activeContent: contentToOpen, isOpen: true, ...updates };
	});

	// Expand audio player every time it's opened
	setActiveAudioContainerMode(AUDIO_CONTAINER_MODES.EXPANDED);
};

export const activatePendingContent = (): void => {
	audioPlayerState.set((prevState) => {
		if (prevState.pendingContent) {
			return {
				...prevState,
				activeContent: prevState.pendingContent,
				pendingContent: null,
				isPlaying: false,
				isPaused: false,
			};
		}
		return prevState;
	});
};

export const clearPendingContent = (): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		pendingContent: null,
	}));
};

export const closeAudioPlayer = (): void => {
	const returnFocusRef = audioPlayerState.get().returnFocusRef;
	audioPlayerState.set(initialState);

	// Restore focus to the element that opened the player
	if (returnFocusRef?.current) {
		returnFocusRef.current.focus();
	}
};

/**
 * Hiding the player is distinct from closing the player. Closing the player resets the state to initial values,
 * while hiding the player sets the player to hidden and keeps the state intact.
 */
export const hideAudioPlayer = (): void => {
	const returnFocusRef = audioPlayerState.get().returnFocusRef;

	audioPlayerState.set((prevState) => {
		// If the player is not playing or paused, reset the state
		// This works like the closeAudioPlayer action
		if (!prevState.isPaused && !prevState.isPlaying) {
			return initialState;
		}

		if (!prevState.isOpen) {
			return prevState;
		}

		let updates: Partial<AudioPlayerState> = {};

		// If the player is playing, pause it before hiding
		if (prevState.isPlaying) {
			updates.isPlaying = false;
			updates.isPaused = true;
		}

		updates.isHidden = true;

		return { ...prevState, ...updates };
	});

	// Restore focus to the element that opened the player
	if (returnFocusRef?.current) {
		returnFocusRef.current.focus();
	}
};

export const setIsPlaying = (isPlaying: boolean): void => {
	audioPlayerState.set((prevState) => ({ ...prevState, isPlaying }));
};

export const setIsPaused = (isPaused: boolean): void => {
	audioPlayerState.set((prevState) => ({ ...prevState, isPaused }));
};

// Convenience action to update multiple playback properties at once
export const updatePlayerState = (updates: Partial<AudioPlayerState>): void => {
	audioPlayerState.set((prevState) => ({ ...prevState, ...updates }));
};

export const setCustomization = (newCustomization: AudioPlayerState['customization']): void => {
	audioPlayerState.set((prevState) => {
		const { customization } = prevState;
		const updatedCustomization = { ...customization, ...newCustomization };

		return { ...prevState, customization: updatedCustomization };
	});
};

export const getCustomization = (): {
	tone: ConfluenceTone;
	length: ConfluenceLength;
	playbackSpeed: number;
} => {
	return audioPlayerState.get().customization;
};

export const getActiveContent = (): ActiveContent => {
	return audioPlayerState.get().activeContent;
};

export const setSupportingContent = (
	supportingContent: ActiveContent['supportingContent'],
): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		activeContent: { ...prevState.activeContent, supportingContent },
	}));
};

export const getSupportingContent = (): ContentDetails[] => {
	return audioPlayerState.get().activeContent.supportingContent;
};

export const setPlaybackSpeed = (playbackSpeed: number): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		customization: { ...prevState.customization, playbackSpeed },
	}));
};

export const setTrackSource = (trackSource: TrackSourceType): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		activeContent: { ...prevState.activeContent, trackSource },
	}));
};

export const resetPlaylistDefaultExpanded = (): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		playlistDefaultExpanded: false,
	}));
};

export const resetPlayImmediately = (): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		playImmediately: false,
	}));
};

export const setPlaylistHasUnavailableContent = (playlistHasUnavailableContent: boolean): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		playlistHasUnavailableContent,
	}));
};

export const setInterimSupportingContent = (interimSupportingContent: ContentDetails[]): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		activeContent: { ...prevState.activeContent, interimSupportingContent },
	}));
};

export const getInterimSupportingContent = (): ContentDetails[] | undefined => {
	return audioPlayerState.get().activeContent.interimSupportingContent;
};

export const isSourceContentDiverged = (
	activeSupportingContent: ContentDetails[],
	localAdditionalResources: ContentDetails[],
): boolean => {
	// Handle null/undefined arrays by treating them as empty arrays
	const activeContent = activeSupportingContent || [];
	const localContent = localAdditionalResources || [];

	const haveSourceCountsChanged = localContent.length !== activeContent.length;

	const haveSourceContentsChanged = activeContent.some(
		(resource, index) => localContent?.[index]?.ari !== resource.ari,
	);

	return haveSourceCountsChanged || haveSourceContentsChanged;
};

export const setHasOpenDropdownMenu = (hasOpenDropdownMenu: boolean): void => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		hasOpenDropdownMenu,
	}));
};
